import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Input, NativeSelect, NativeSelectOption, Spinner, Textarea } from "@rakazo/ui-web";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSpaceQueries } from "../lib/query";
import { useSpaceRpc } from "../lib/space-rpc";
import { errorText } from "../lib/user-error";

export function SemanticMemoryBrowser({ fixedBotId }: { fixedBotId?: string }) {
  const queries = useSpaceQueries();
  const { t } = useLingui();
  const bots = useQuery(queries.bots.list.queryOptions());
  const [selection, setSelection] = useState("");
  const [scope, setScope] = useState<"isolated" | "shared">("isolated");
  const [fact, setFact] = useState("");
  const rpc = useSpaceRpc();
  const configurableBots = (bots.data ?? []).filter((bot) => bot.canConfigure !== false);
  const botId = fixedBotId || selection || configurableBots[0]?.id || "";
  const members = useQuery({
    ...queries.bots.configurationMembers.queryOptions({ input: { botId } }),
    enabled: Boolean(botId),
  });
  const [selectedUser, setSelectedUser] = useState("");
  const userId = selectedUser || members.data?.[0]?.userId;
  const identity = useQuery({
    ...queries.bots.identity.queryOptions({ input: { botId } }),
    enabled: Boolean(botId),
  });
  const save = useMutation({
    mutationFn: async (input: { content: string; scope: "isolated" | "shared" }) =>
      rpc.memory.saveFact({
        botId,
        ...input,
        ...(input.scope === "isolated" && userId ? { userId } : {}),
      }),
    onSuccess: () => {
      setFact("");
      void page.refetch();
    },
  });
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const page = useQuery({
    ...queries.memory.browse.queryOptions({
      input: {
        scope,
        botId,
        ...(scope === "isolated" && userId ? { userId } : {}),
        ...(query ? { query } : {}),
        ...(cursors.length ? { cursor: cursors[cursors.length - 1] } : {}),
        limit: query ? 50 : 25,
      },
    }),
    enabled: Boolean(botId),
    refetchOnMount: "always",
    retry: false,
    gcTime: 0,
  });

  function reset() {
    setCursors([]);
  }

  return (
    <section
      className="rounded-xl border border-border px-4 py-4"
      data-testid="semantic-memory-browser"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-heading3 font-medium text-foreground">
          <Trans>Saved memories</Trans>
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={page.isFetching}
          onClick={() => void page.refetch()}
        >
          <Trans>Refresh</Trans>
        </Button>
      </div>
      <div className="mt-3 space-y-3">
        {!fixedBotId ? (
          <NativeSelect
            aria-label={t`Bot`}
            className="w-full"
            value={botId}
            onChange={(event) => {
              setSelection(event.target.value);
              setSelectedUser("");
              reset();
            }}
          >
            {configurableBots.map((bot) => (
              <NativeSelectOption key={bot.id} value={bot.id}>
                {bot.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        ) : null}
        <NativeSelect
          aria-label={t`Memory scope`}
          value={scope}
          onChange={(event) => {
            setScope(event.target.value as "isolated" | "shared");
            reset();
          }}
        >
          <NativeSelectOption value="isolated">
            <Trans>Private</Trans>
          </NativeSelectOption>
          {identity.data ? (
            <NativeSelectOption value="shared">
              <Trans>Company</Trans>
            </NativeSelectOption>
          ) : null}
        </NativeSelect>
        {scope === "isolated" && (members.data?.length ?? 0) > 1 ? (
          <NativeSelect
            aria-label={t`User memory`}
            value={userId ?? ""}
            onChange={(e) => {
              setSelectedUser(e.target.value);
              reset();
            }}
          >
            {(members.data ?? []).map((member) => (
              <NativeSelectOption key={member.userId} value={member.userId}>
                {member.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        ) : null}
        {save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorText(save.error)}
          </p>
        ) : null}
        {identity.data ? (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (fact.trim()) save.mutate({ content: fact.trim(), scope });
            }}
          >
            <Textarea
              aria-label={t`New memory`}
              placeholder={t`New memory`}
              value={fact}
              maxLength={8000}
              onChange={(event) => setFact(event.target.value)}
            />
            <Button type="submit" variant="secondary" disabled={!fact.trim() || save.isPending}>
              {scope === "shared" ? (
                <Trans>Share with company</Trans>
              ) : (
                <Trans>Save privately</Trans>
              )}
            </Button>
          </form>
        ) : null}
        {bots.isError ? (
          <p role="alert" className="text-[13px] text-destructive">
            <Trans>Could not load bots.</Trans>
          </p>
        ) : null}
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(draft.trim());
            reset();
          }}
        >
          <Input
            type="search"
            aria-label={t`Search memories`}
            placeholder={t`Search memories`}
            value={draft}
            maxLength={1000}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button type="submit" variant="secondary">
            <Trans>Search</Trans>
          </Button>
          {query ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft("");
                setQuery("");
                reset();
              }}
            >
              <Trans>Clear</Trans>
            </Button>
          ) : null}
        </form>
        <div aria-live="polite" aria-busy={page.isFetching}>
          {page.isPending ? <Spinner aria-label={t`Loading memories`} /> : null}
          {page.isError ? (
            <p role="alert" className="text-[13px] text-destructive">
              <Trans>Could not load memories. Try refreshing.</Trans>
            </p>
          ) : null}
          {page.isSuccess && page.data.items.length === 0 ? (
            <p className="py-3 text-[13px] text-muted-foreground">
              {query ? (
                <Trans>No matching memories.</Trans>
              ) : (
                <Trans>No saved memories in this scope.</Trans>
              )}
            </p>
          ) : null}
          {page.isSuccess ? (
            <ul className="divide-y divide-border">
              {page.data.items.map((item) => {
                const date = item.updatedAt ?? item.createdAt;
                const validDate = date && Number.isFinite(Date.parse(date)) ? date : null;
                return (
                  <li key={item.id} className="py-3">
                    <p
                      className="whitespace-pre-wrap break-words text-[14px] leading-relaxed text-foreground"
                      dir="auto"
                    >
                      {item.memory}
                    </p>
                    {scope === "isolated" && identity.data ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={save.isPending || item.memory.length > 8000}
                        onClick={() => save.mutate({ content: item.memory, scope: "shared" })}
                      >
                        <Trans>Share with company</Trans>
                      </Button>
                    ) : null}
                    {validDate ? (
                      <time
                        className="mt-1 block text-xs text-muted-foreground"
                        dateTime={validDate}
                      >
                        {new Date(validDate).toLocaleString()}
                      </time>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        {cursors.length > 0 || page.data?.nextCursor ? (
          <div className="flex justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={cursors.length === 0 || page.isFetching}
              onClick={() => setCursors((current) => current.slice(0, -1))}
            >
              <Trans>Previous</Trans>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!page.data?.nextCursor || page.isFetching}
              onClick={() => {
                const cursor = page.data?.nextCursor;
                if (cursor) setCursors((current) => [...current, cursor]);
              }}
            >
              <Trans>Next</Trans>
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
